// src/pages/SupplyChainDetail.jsx
//
// Per-supplier risk dashboard, driven end-to-end by the live
// /supply_chain/supply_chain_score.php endpoint.
//
// Response shape (new lean contract):
//   - domain, score, grade, delta, computed_at, cached, record_count
//   - indices.{breach, malware, combolist}: { grade, index, timeline[],
//       total_records, (critical_records for malware/combolist),
//       (cookie:{machines, valid_sessions, available, signal_bonus} on malware) }
//   - applications[]: unified app surface with tier=critical|corporate|database,
//       application, records, infected_devices, latest, sources[]
//   - compromised_machines.{available, machines[]}
//   - critical_apps_monitored[]: catalog of monitored hostnames (large)
//   - findings_summary[]: analyst narrative bullets
//
// Layout reads top-down as an RF / Mandiant intel brief:
//   1. Vendor header (identity + grade + score)
//   2. Executive summary (verdict + analyst takeaways)
//   3. Risk profile (3 source-type indices with sparkline trends)
//   4. Critical identity exposures (critical apps + live sessions)
//   5. Account / database exposures (unified `applications` filtered by tier)
//   6. Monitored critical apps catalogue (collapsed)
//   7. Live API debug (collapsed)

import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  RefreshCw,
  AlertTriangle,
  AlertOctagon,
  Database,
  Cookie,
  Building2,
  ShieldAlert,
  Bug,
  Layers,
  Globe,
  ArrowRight,
  Network,
} from "lucide-react";
import { redirectToLogin } from "../utils/auth";
import Sidebar from "../components/Sidebar";
import DetailSidebar from "../components/DetailSidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { useSupplierSecurityAccess } from "../utils/brandProtection";
import { FLAG, COUNTRY_OPTIONS, loadCompanies } from "../data/supplyChainCompanies";

const SUPPLY_CHAIN_SCORE_URL =
  "https://whiteintel.io/api/regular/app/v2/supply_chain/supply_chain_score.php";

// Same endpoint Global Search uses. We hit it with searchType
// "corporateRecords" + the vendor's domain so analysts can dive straight
// into the raw credential records without leaving the supplier page.
const GLOBAL_SEARCH_URL =
  "https://whiteintel.io/api/regular/app/v2/globalsearch/global_search_v6.php";

function getJwtToken() {
  try {
    return (
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("jwtToken") ||
      window.WHITEINTEL_TOKEN ||
      ""
    );
  } catch {
    return "";
  }
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Visual tokens â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

const panel = "rounded-md border border-sky-900/30 bg-[#0a1224]/70";
const panelHeader =
  "px-4 py-3 border-b border-sky-900/30 flex items-center justify-between gap-3";
const panelTitle = "text-[13px] font-semibold text-neutral-100";

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Tone helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

function indexTone(score) {
  if (score >= 75) return { ring: "#f43f5e", text: "text-rose-300", spark: "#f43f5e" };
  if (score >= 50) return { ring: "#f59e0b", text: "text-amber-300", spark: "#f59e0b" };
  if (score >= 25) return { ring: "#eab308", text: "text-yellow-300", spark: "#eab308" };
  return { ring: "#10b981", text: "text-emerald-300", spark: "#10b981" };
}

function gradeTone(grade) {
  const g = String(grade || "").toUpperCase();
  if (g === "F" || g === "D")
    return { text: "text-rose-300", border: "border-rose-500/40", bg: "bg-rose-500/10" };
  if (g === "C")
    return { text: "text-amber-300", border: "border-amber-500/40", bg: "bg-amber-500/10" };
  if (g === "B")
    return { text: "text-yellow-300", border: "border-yellow-500/40", bg: "bg-yellow-500/10" };
  if (g === "A")
    return { text: "text-emerald-300", border: "border-emerald-500/40", bg: "bg-emerald-500/10" };
  return { text: "text-neutral-300", border: "border-neutral-700/40", bg: "bg-neutral-700/10" };
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Formatters â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

function fmtInt(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "â€”";
  return Number(n).toLocaleString();
}
// Backend can return 0 for a vendor that has no recent dark-web signal at all.
// Reading "0" makes analysts think we didn't measure anything, so we surface a
// small non-zero baseline (5) instead. Only applied to the composite risk
// score — delta + per-source indices keep their literal value.
function displayRiskScore(n) {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  if (num === 0) return "5";
  return fmtScore(num);
}

function fmtScore(n) {
  if (n === null || n === undefined) return "â€”";
  const num = Number(n);
  if (Number.isNaN(num)) return "â€”";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: num % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 2,
  });
}
function fmtDate(s) {
  if (!s) return "â€”";
  return s;
}

// Combolist + Stealer are surfaced as a single "Stealer" surface for analysts.
function normalizeSourceLabel(t) {
  const s = String(t || "").toLowerCase();
  if (s === "combolist" || s === "stealer") return "Stealer";
  if (s === "breach") return "Breach";
  if (!t) return "â€”";
  return String(t);
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Gauge (0â€“100 index) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

function Gauge({ score, size = "md", label }) {
  const dim = size === "lg" ? 128 : size === "md" ? 72 : 28;
  const stroke = size === "lg" ? 6 : size === "md" ? 5 : 3;
  const fontSize = size === "lg" ? 28 : size === "md" ? 18 : 10;
  const labelSize = size === "lg" ? 11 : 9;
  const cx = dim / 2;
  const r = (dim - stroke) / 2;
  const c = 2 * Math.PI * r;
  const safeScore = Math.max(0, Math.min(100, Number(score) || 0));
  const offset = c - (safeScore / 100) * c;
  const { ring, text } = indexTone(safeScore);
  return (
    <div className="relative shrink-0" style={{ width: dim, height: dim }}>
      <svg viewBox={`0 0 ${dim} ${dim}`} className="-rotate-90" width={dim} height={dim}>
        <circle cx={cx} cy={cx} r={r} fill="none" stroke="rgba(148,163,184,0.15)" strokeWidth={stroke} />
        <circle
          cx={cx}
          cy={cx}
          r={r}
          fill="none"
          stroke={ring}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-tight">
        <div className={`font-bold ${text}`} style={{ fontSize }}>
          {Math.round(safeScore)}
        </div>
        {label && size !== "sm" && (
          <div className="text-neutral-500 mt-0.5 text-center" style={{ fontSize: labelSize }}>
            {label}
          </div>
        )}
      </div>
    </div>
  );
}

// Mirrors src/pages/Dashboard.jsx — Google's S2 favicon service. Returns a
// real favicon for known domains and Google's own globe glyph for unknowns.
// We still wire `onError` so an actual network failure also falls back.
function DomainFavicon({ domain, size = 16, className = "" }) {
  const [failed, setFailed] = useState(false);
  if (!domain || failed) {
    return (
      <Globe
        aria-hidden
        className={`shrink-0 text-neutral-500 ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=${size * 2}`}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={`shrink-0 rounded-sm ${className}`}
    />
  );
}

function MetaItem({ label, value, valueExtra }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px]">
      <span className="text-neutral-500">{label}:</span>
      <span className="text-neutral-200 font-medium">{value}</span>
      {valueExtra}
    </span>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Vendor metadata fallback â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

function deriveIndustry(name) {
  const lower = String(name || "").toLowerCase();
  if (/health|ehr|patient|telehealth|lab|clinic|medical/.test(lower)) return "Healthcare";
  if (/pay|stripe|bank|cycle|revenue|invoice/.test(lower)) return "Financial Services";
  if (/network|defense|access|cloud|security|identity|risk/.test(lower)) return "Cybersecurity";
  if (/data|monitor|datadog|log|message/.test(lower)) return "Developer Infrastructure";
  if (/regulatory|edi|vendor|portal/.test(lower)) return "Supply Chain & Compliance";
  return "Software & SaaS";
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Sparkline â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

// Simple SVG sparkline keyed on (Year, Month, Count). We only show the trailing
// `months` window so the chart stays readable when the backend returns 5+ years
// of history. `null` â†’ empty placeholder.
function Sparkline({ timeline, months = 18, color = "#38bdf8", height = 42 }) {
  const safe = Array.isArray(timeline) ? timeline : [];
  // Sort by year/month and take the last N points
  const sorted = [...safe]
    .filter((p) => p && p.Year !== undefined && p.Month !== undefined)
    .sort((a, b) => (a.Year - b.Year) * 12 + (a.Month - b.Month))
    .slice(-months);

  if (sorted.length === 0) {
    return (
      <div
        className="rounded border border-sky-900/30 bg-[#06101e] flex items-center justify-center text-[10.5px] text-neutral-600"
        style={{ height }}
      >
        No timeline
      </div>
    );
  }

  const values = sorted.map((p) => Number(p.Count) || 0);
  const max = Math.max(...values, 1);
  const w = 100;
  const h = 100;
  const pad = 4;
  const step = sorted.length > 1 ? (w - pad * 2) / (sorted.length - 1) : 0;
  const points = sorted.map((p, i) => {
    const x = pad + step * i;
    const y = h - pad - ((Number(p.Count) || 0) / max) * (h - pad * 2);
    return [x, y];
  });
  const path = points.map((pt, i) => `${i === 0 ? "M" : "L"} ${pt[0]} ${pt[1]}`).join(" ");
  const area = `${path} L ${points[points.length - 1][0]} ${h - pad} L ${points[0][0]} ${h - pad} Z`;
  const last = sorted[sorted.length - 1];
  const peak = sorted.reduce((acc, p) => ((p.Count || 0) > (acc.Count || 0) ? p : acc), sorted[0]);

  return (
    <div className="w-full">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className="w-full"
        style={{ height }}
      >
        <path d={area} fill={color} opacity="0.12" />
        <path d={path} fill="none" stroke={color} strokeWidth="1.2" strokeLinejoin="round" strokeLinecap="round" />
      </svg>
      <div className="mt-1 flex items-center justify-between text-[10.5px] text-neutral-500 tabular-nums">
        <span>
          Last: <span className="text-neutral-300">{last.Count.toLocaleString()}</span>{" "}
          ({last.Year}-{String(last.Month).padStart(2, "0")})
        </span>
        <span>
          Peak: <span className="text-neutral-300">{peak.Count.toLocaleString()}</span>{" "}
          ({peak.Year}-{String(peak.Month).padStart(2, "0")})
        </span>
      </div>
    </div>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Executive Summary â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

function ExecutiveSummaryCard({ data }) {
  const grade = data?.grade;
  const gt = gradeTone(grade);
  const score = data?.score;
  const delta = Number(data?.delta || 0);
  const recordCount = data?.record_count;
  const findings = Array.isArray(data?.findings_summary) ? data.findings_summary : [];
  const computedAt = data?.computed_at;
  const cached = data?.cached;
  // Clean-record state — grade A AND a literal 0 composite. We don't want
  // the analyst to read this as "vendor is safe": dark-web exposure is one
  // of many attack vectors, and an A here just means we found no leaked
  // material on this domain, not that no attack can land.
  const cleanRecord = String(grade || "").toUpperCase() === "A" && Number(score) === 0;

  const deltaIcon =
    delta > 0 ? (
      <ArrowUpRight className="h-3.5 w-3.5 text-rose-400" />
    ) : delta < 0 ? (
      <ArrowDownRight className="h-3.5 w-3.5 text-emerald-400" />
    ) : null;

  return (
    <section className={`${panel}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* Left: Grade + risk + score */}
        <div className="lg:col-span-5 px-5 py-5 lg:border-r border-sky-900/30 space-y-4">
          <div className="flex items-start gap-4">
            <div
              className={`h-20 w-20 shrink-0 rounded-md border ${gt.border} ${gt.bg} flex items-center justify-center`}
            >
              <span className={`text-[42px] font-bold leading-none ${gt.text}`}>
                {grade || "â€”"}
              </span>
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                Vendor Risk Grade
              </div>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1 rounded-full border ${gt.border} ${gt.bg} px-2 py-0.5 text-[11px] font-semibold ${gt.text}`}
                >
                  <ShieldAlert className="h-3 w-3" />
                  {grade ? `Grade ${grade}` : "â€”"}
                </span>
                {cached !== undefined && (
                  <span className="text-[10.5px] text-neutral-500">
                    {cached ? "Cached" : "Fresh"}
                  </span>
                )}
              </div>
              <div className="mt-2 text-[12px] text-neutral-400 leading-snug">
                {computedAt ? (
                  <>Computed <span className="text-neutral-200 tabular-nums">{computedAt}</span></>
                ) : (
                  "â€”"
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                Risk Score
              </div>
              <div className="mt-0.5 flex items-center gap-1.5">
                <span className="text-[26px] font-bold tabular-nums text-neutral-100 leading-none">
                  {displayRiskScore(score)}
                </span>
                {deltaIcon}
              </div>
              <div className="mt-1 text-[11.5px] text-neutral-500">
                {delta === 0 ? (
                  <span>No change since last computation</span>
                ) : (
                  <span
                    className={delta > 0 ? "text-rose-300 tabular-nums" : "text-emerald-300 tabular-nums"}
                  >
                    {delta > 0 ? "+" : ""}
                    {fmtScore(delta)} vs prior
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                Records Exposed
              </div>
              <div className="mt-0.5 text-[26px] font-bold tabular-nums text-neutral-100 leading-none">
                {fmtInt(recordCount)}
              </div>
              <div className="mt-1 text-[11.5px] text-neutral-500">
                Across all sources
              </div>
            </div>
          </div>
        </div>

        {/* Right: Findings narrative */}
        <div className="lg:col-span-7 px-5 py-5">
          <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold flex items-center gap-1.5">
            <AlertOctagon className="h-3 w-3" />
            Key Findings
          </div>
          {findings.length > 0 ? (
            <ul className="mt-3 space-y-2.5">
              {findings.map((line, i) => (
                <li key={i} className="flex gap-2.5 text-[13px] leading-relaxed text-neutral-200">
                  <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-sky-400" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-[13px] text-neutral-500">
              No analyst findings returned for this vendor.
            </p>
          )}

          {cleanRecord && (
            <aside
              role="note"
              className="mt-4 rounded-r-md border-l-2 border-sky-500/40 bg-[#0b1426]/60 px-4 py-3"
            >
              <div className="text-[10px] uppercase tracking-[0.18em] text-sky-300/70 font-semibold">
                Methodology
              </div>
              <p className="mt-1.5 text-[12.5px] leading-relaxed text-neutral-300">
                This score reflects WhiteIntel's dark-web telemetry —
                credential leaks, infostealer logs, breach mentions, and
                exposed session cookies. A clean record confirms no exposed
                material was found at the time of scoring. Defense in depth
                remains essential, as adversaries also pivot through phishing,
                supply-chain compromise, insider risk, and zero-day
                exploitation.
              </p>
            </aside>
          )}
        </div>
      </div>
    </section>
  );
}

/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Risk Profile (3 source-type indices) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

const SOURCE_TYPES = [
  { key: "malware", label: "Malware", icon: Bug, description: "Stealer-log exposures" },
  { key: "combolist", label: "Combolist", icon: Layers, description: "Credential dumps" },
  { key: "breach", label: "Breach", icon: Database, description: "Third-party DB leaks" },
];

function RiskProfileCard({ data }) {
  return (
    <section className={panel}>
      <div className={panelHeader}>
        <div className="flex items-center gap-2">
          <span className={panelTitle}>Risk Profile</span>
          <span className="text-[11px] text-neutral-500">
            By exposure source
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-y lg:divide-y-0 divide-sky-900/30">
        {SOURCE_TYPES.map((t) => {
          const entry = data?.indices?.[t.key];
          if (!entry) {
            return (
              <div key={t.key} className="px-5 py-5 text-[12.5px] text-neutral-500">
                {t.label}: no data
              </div>
            );
          }
          const Icon = t.icon;
          const tone = indexTone(entry.index);
          const gtone = gradeTone(entry.grade);
          return (
            <div key={t.key} className="px-5 py-5 space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon className="h-3.5 w-3.5 text-sky-300 shrink-0" />
                  <span className="text-[13px] font-semibold text-neutral-100">{t.label}</span>
                  <span
                    className={`inline-flex items-center justify-center rounded border ${gtone.border} ${gtone.bg} px-1.5 py-0 text-[10.5px] font-bold ${gtone.text}`}
                  >
                    {entry.grade || "â€”"}
                  </span>
                </div>
              </div>

              {/* Gauge + records */}
              <div className="flex items-center gap-4">
                <Gauge score={entry.index} size="md" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-medium">
                    Total records
                  </div>
                  <div className="text-[18px] font-semibold text-neutral-100 tabular-nums leading-tight">
                    {fmtInt(entry.total_records)}
                  </div>
                  {entry.critical_records !== undefined && (
                    <div className="mt-1 text-[10.5px] text-neutral-500">
                      <span className="text-rose-300 tabular-nums">
                        {fmtInt(entry.critical_records)}
                      </span>{" "}
                      on critical assets
                    </div>
                  )}
                </div>
              </div>

              {/* Trailing 18-month sparkline */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-medium mb-1">
                  Trailing 18 months
                </div>
                <Sparkline timeline={entry.timeline} months={18} color={tone.spark} />
              </div>

              {/* Malware column also surfaces session-cookie signal, the
                  highest-severity sub-indicator of stealer infections. Only
                  rendered when the API reports active cookie telemetry. */}
              {t.key === "malware" && entry.cookie?.available && (entry.cookie.machines > 0 || entry.cookie.valid_sessions > 0) && (
                <div className="rounded-md border border-rose-500/30 bg-rose-500/[0.08] px-3 py-2">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-rose-300/80 font-semibold">
                    <Cookie className="h-3 w-3" />
                    Live Sessions
                  </div>
                  <div className="mt-1 flex items-baseline gap-3">
                    <span className="text-[16px] font-bold tabular-nums text-rose-200 leading-none">
                      {fmtInt(entry.cookie.valid_sessions)}
                    </span>
                    <span className="text-[11px] text-neutral-400">
                      MFA-bypassing Â· {fmtInt(entry.cookie.machines)} machines
                    </span>
                  </div>
                </div>
              )}

              <div className="text-[11px] text-neutral-500 leading-snug">{t.description}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ────────────────────────── Latest Corporate Exposures ────────────────────────── */

// Backend now returns a single `latest_corporate_exposures` tail window —
// most-recent N applications with recorded credential leaks. No tier field,
// no infected-device counts, no critical/database split. Render as a simple
// table; source labels are normalized to "Stealer" / "Breach".
function LatestCorporateExposuresPanel({ data }) {
  const rows = Array.isArray(data?.latest_corporate_exposures)
    ? data.latest_corporate_exposures
    : [];

  return (
    <section className={panel}>
      <div className={panelHeader}>
        <div className="flex items-center gap-2">
          <Building2 className="h-3.5 w-3.5 text-sky-300" />
          <span className={panelTitle}>Latest Corporate Exposures</span>
          <span className="text-[11px] text-neutral-500">
            Most recent credential leaks across vendor accounts
          </span>
        </div>
        <span className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10.5px] text-amber-200">
          {rows.length} {rows.length === 1 ? "entry" : "entries"}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-[12.5px] text-neutral-500">
          No recent corporate exposures.
        </div>
      ) : (
        <div className="max-h-[500px] overflow-auto">
          <table className="w-full text-[12.5px]">
            <thead className="bg-white/[0.02] text-neutral-400 sticky top-0">
              <tr>
                <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-[10.5px]">Source</th>
                <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-[10.5px]">Application</th>
                <th className="px-4 py-2 text-right font-medium uppercase tracking-wider text-[10.5px]">Records</th>
                <th className="px-4 py-2 text-left font-medium uppercase tracking-wider text-[10.5px]">Latest</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const labels = Array.from(
                  new Set((row.sources || []).map(normalizeSourceLabel))
                );
                return (
                  <tr
                    key={i}
                    className={`border-t border-sky-900/20 hover:bg-sky-900/15 ${i % 2 === 0 ? "" : "bg-[#091020]/30"}`}
                  >
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {labels.map((s) => (
                          <span
                            key={s}
                            className="inline-flex items-center rounded border border-sky-900/40 bg-sky-500/10 px-1.5 py-0.5 text-[10.5px] text-sky-200"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sky-300">
                      <div className="flex items-center gap-2 min-w-0">
                        <DomainFavicon domain={row.application} size={16} />
                        <span className="break-all">{row.application}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right text-neutral-100 font-medium tabular-nums">
                      {fmtInt(row.records)}
                    </td>
                    <td className="px-4 py-2 text-neutral-300 tabular-nums">{fmtDate(row.latest)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ────────────────────────── Credentials Tab ──────────────────────────
 *
 * Calls the same Global Search endpoint analysts use elsewhere, scoped to
 * the vendor's domain + corporate records. Results mirror the Global Search
 * desktop table (Id / Leaked accounts / Applications / Type / Last seen /
 * View) so analysts feel at home, and the "View" link opens the same
 * DetailSidebar with `detail_type: "corporateRecords"` for record drill-down.
 */

function CredentialsTab({ decoded, onView }) {
  const [state, setState] = useState({
    loading: false,
    err: null,
    results: [],
    page: 1,
    hasMore: false,
    loaded: false,
  });

  const fetchPage = async ({ append = false } = {}) => {
    const nextPage = append ? state.page + 1 : 1;
    setState((s) => ({ ...s, loading: true, err: null }));
    try {
      const res = await fetch(GLOBAL_SEARCH_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({
          query: decoded,
          searchType: "corporateRecords",
          filters: [],
          page: nextPage,
          sort_by: "log_date",
        }),
      });
      if (res.status === 401) {
        redirectToLogin();
        return;
      }
      const text = await res.text();
      let payload = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {
        payload = null;
      }
      if (!res.ok) {
        setState((s) => ({
          ...s,
          loading: false,
          err: (payload && (payload.message || payload.error)) || `HTTP ${res.status}`,
        }));
        return;
      }
      const rows = Array.isArray(payload?.results) ? payload.results : [];
      setState((s) => ({
        loading: false,
        err: null,
        results: append ? [...s.results, ...rows] : rows,
        page: nextPage,
        hasMore: !!payload?.has_more,
        loaded: true,
      }));
    } catch (e) {
      setState((s) => ({ ...s, loading: false, err: e?.message || "Network error" }));
    }
  };

  // Initial fetch on mount (the tab is only mounted when active, so this
  // keeps the request lazy without an explicit gate).
  useEffect(() => {
    fetchPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decoded]);

  return (
    <section className={panel}>
      {/* Header strip */}
      <div className={panelHeader}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={panelTitle}>Corporate Credential Records</span>
          <span className="text-[11px] text-neutral-500 truncate">
            for <span className="text-neutral-300 font-mono">{decoded}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center rounded-full border border-sky-900/40 bg-sky-500/10 px-2 py-0.5 text-[10.5px] text-sky-200 tabular-nums">
            {state.results.length} {state.hasMore ? "+" : ""} loaded
          </span>
          <button
            type="button"
            onClick={() => fetchPage()}
            disabled={state.loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 hover:bg-sky-900/30 px-2 py-1 text-[12px] text-neutral-300 hover:text-sky-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-3 w-3 ${state.loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="overflow-auto">
        <table className="w-full table-fixed">
          <thead className="bg-[#0a1224]/95 sticky top-0">
            <tr className="border-b border-sky-900/30">
              <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 w-24">Id</th>
              <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 w-64">Leaked accounts</th>
              <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 w-64">Applications</th>
              <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 w-32">Type</th>
              <th className="px-4 py-2 text-left text-[10px] uppercase tracking-wider font-semibold text-neutral-500 w-40">Last seen</th>
              <th className="px-4 py-2 w-24" aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-sky-900/30">
            {state.results.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-neutral-500 text-[13px]">
                  {state.loading ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-sky-300" />
                      Loading credential records…
                    </span>
                  ) : state.err ? (
                    <span className="text-rose-300">Failed to load results: {state.err}</span>
                  ) : (
                    <>No corporate credential records found for this vendor.</>
                  )}
                </td>
              </tr>
            ) : (
              state.results.map((r, idx) => (
                <tr key={`${r.log_id}-${idx}`} className="hover:bg-white/[0.02] transition-colors">
                  <td
                    className="px-4 py-2.5 align-middle text-[12px] text-neutral-500 font-mono tabular-nums truncate"
                    title={r.log_id}
                  >
                    {r.log_id}
                  </td>
                  <td
                    className="px-4 py-2.5 align-middle text-[13px] text-white font-mono truncate"
                    title={r.username}
                  >
                    {r.username}
                  </td>
                  <td
                    className="px-4 py-2.5 align-middle text-[13px] text-neutral-300 truncate"
                    title={r.applications}
                  >
                    {r.applications}
                  </td>
                  <td className="px-4 py-2.5 align-middle">
                    <span
                      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] uppercase tracking-wider font-mono font-semibold ${
                        r.data_type === "stealer"
                          ? "bg-rose-500/[0.08] text-rose-300 border-rose-500/30"
                          : r.data_type === "database"
                          ? "bg-violet-500/[0.08] text-violet-300 border-violet-500/30"
                          : "bg-amber-500/[0.08] text-amber-300 border-amber-500/30"
                      }`}
                    >
                      {r.data_type}
                    </span>
                  </td>
                  <td
                    className="px-4 py-2.5 align-middle text-[12px] text-neutral-400 font-mono tabular-nums truncate"
                    title={r.last_seen || "—"}
                  >
                    {r.last_seen || "—"}
                  </td>
                  <td className="px-4 py-2.5 align-middle text-right">
                    <button
                      onClick={() => onView(r)}
                      className="inline-flex items-center gap-1 text-[12px] text-sky-300 hover:text-sky-200 transition-colors"
                      aria-label="View details"
                    >
                      View
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {state.results.length > 0 && (
        <div className="border-t border-sky-900/30 px-4 py-3 flex justify-center">
          <button
            onClick={() => fetchPage({ append: true })}
            disabled={!state.hasMore || state.loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-sky-900/40 bg-neutral-900/40 px-3 py-1.5 text-[12px] text-neutral-300 hover:text-white hover:bg-neutral-900 disabled:opacity-40 disabled:hover:bg-neutral-900/40 disabled:hover:text-neutral-300 transition-colors"
          >
            {state.loading ? "Loading…" : state.hasMore ? "Load more" : "No more results"}
          </button>
        </div>
      )}
    </section>
  );
}


/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

function SupplierUpgradeRequiredCta() {
  const navigate = useNavigate();
  return (
    <div className="max-w-2xl mx-auto mt-12 px-4">
      <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 backdrop-blur-md shadow-[0_0_10px_rgba(56,189,248,0.05)] p-8 text-center">
        <div className="mx-auto inline-flex rounded-full border border-sky-500/30 bg-sky-500/10 p-3 mb-4">
          <Network className="h-6 w-6 text-sky-300" />
        </div>
        <h2 className="text-xl font-semibold text-white">
          Supplier Security is a premium module
        </h2>
        <p className="mt-2 text-sm text-neutral-400 leading-relaxed">
          Continuous credential-exposure scoring for your vendors and partners.
          Available on{" "}
          <span className="text-sky-200 font-medium">Enterprise</span>{" "}
          (64 suppliers),{" "}
          <span className="text-sky-200 font-medium">Threat Intelligence</span>{" "}
          (128), and{" "}
          <span className="text-sky-200 font-medium">Extended Threat Intelligence</span>{" "}
          (256).
        </p>
        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/upgrades")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-600/40 bg-sky-500/15 px-4 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/25 transition-colors"
          >
            View plans
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="rounded-lg border border-sky-900/40 px-4 py-2 text-sm text-neutral-200 hover:text-white hover:bg-white/[0.04] transition-colors"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SupplyChainDetail() {
  const navigate = useNavigate();
  const { domain } = useParams();
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  const supplierAccess = useSupplierSecurityAccess();

  const decoded = decodeURIComponent(domain || "");
  const company = useMemo(() => {
    const all = loadCompanies();
    return all.find((c) => c.domain === decoded);
  }, [decoded]);

  const [scoreState, setScoreState] = useState({
    loading: false,
    error: null,
    response: null,
    requestedAt: null,
  });

  // Server-side org gate — same trip-wire as the other supplier pages: any
  // endpoint that replies "You must belong to an organization …" flips this
  // and the page shows the upgrade CTA instead of the broken state.
  const [orgGate, setOrgGate] = useState(false);
  const gateActive =
    (!supplierAccess.loading && supplierAccess.available === false) || orgGate;

  // Tab nav + DetailSidebar state. Declared up here so all hooks run before
  // the "Vendor not found" early return below (rules-of-hooks).
  const [activeTab, setActiveTab] = useState("overview");
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [detailInitialParams, setDetailInitialParams] = useState(null);

  const handleCredentialView = (row) => {
    setDetailRow(row);
    setDetailInitialParams({
      id: Number(row?.log_id),
      type: String(row?.data_type || "stealer"),
      detail_type: "corporateRecords",
      query: decoded,
    });
    setDetailOpen(true);
  };

  const fetchScore = async ({ force = false } = {}) => {
    if (!decoded) return;
    setScoreState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await fetch(SUPPLY_CHAIN_SCORE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({ query: decoded, force, detail: true }),
      });
      if (res.status === 401) {
        redirectToLogin();
        return;
      }
      const text = await res.text();
      let payload = null;
      try {
        payload = text ? JSON.parse(text) : null;
      } catch {
        payload = { _raw: text };
      }
      if (!res.ok) {
        const msg =
          (payload && (payload.message || payload.error)) ||
          `Request failed with HTTP ${res.status}`;
        if (/must belong to an organization/i.test(msg)) {
          setOrgGate(true);
          setScoreState({
            loading: false,
            error: null,
            response: null,
            requestedAt: new Date().toISOString(),
          });
          return;
        }
        setScoreState({
          loading: false,
          error: msg,
          response: payload,
          requestedAt: new Date().toISOString(),
        });
        return;
      }
      setScoreState({
        loading: false,
        error: null,
        response: payload,
        requestedAt: new Date().toISOString(),
      });
    } catch (e) {
      setScoreState({
        loading: false,
        error: e?.message || "Network error",
        response: null,
        requestedAt: new Date().toISOString(),
      });
    }
  };

  useEffect(() => {
    if (gateActive) return;
    if (decoded) fetchScore({ force: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decoded, gateActive]);

  const headerMeta = useMemo(() => {
    if (!company) return null;
    const hqLocation =
      COUNTRY_OPTIONS.find((c) => c.code === company.country)?.label || company.country;
    return {
      name: company.name,
      domain: company.domain,
      country: company.country,
      size: company.size,
      hqLocation,
      industry: deriveIndustry(company.name),
    };
  }, [company]);

  if (!company || !headerMeta) {
    return (
      <div className="min-h-screen bg-[#000414] text-white">
        <div
          className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-50"
          style={{ marginLeft: collapsed ? "56px" : "224px" }}
        >
          <div className="px-6 h-10 flex items-center">
            <button
              type="button"
              onClick={() => navigate("/supplier-security")}
              className="inline-flex items-center gap-1.5 text-[12px] text-neutral-400 hover:text-sky-300 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
          </div>
        </div>
        <div className="flex">
          <Sidebar
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            activeKey="supplychain"
            onChange={() => {}}
          />
          <main className="flex-1 px-6 py-10">
            <div className={`${panel} px-6 py-10 text-center max-w-md mx-auto`}>
              <p className="text-[14px] text-neutral-300">Vendor not found.</p>
              <p className="mt-1 text-[12px] text-neutral-500 mb-4">
                <code className="text-sky-300">{decoded}</code> is not in your monitored supply chain.
              </p>
              <button
                type="button"
                onClick={() => navigate("/supplier-security")}
                className="inline-flex items-center gap-1.5 rounded-md border border-sky-600/40 bg-sky-500/15 hover:bg-sky-500/25 px-3 py-1.5 text-[13px] font-medium text-sky-100"
              >
                Back to Supplier Security
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const apiData = scoreState.response && !scoreState.response._raw ? scoreState.response : null;

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Back bar */}
      <div
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-50"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-10 flex items-center">
          <button
            type="button"
            onClick={() => navigate("/supplier-security")}
            className="inline-flex items-center gap-1.5 text-[12px] text-neutral-400 hover:text-sky-300 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
        </div>
      </div>

      {/* Vendor header */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 py-4 flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            {/* Real favicon for the vendor's primary domain. Falls back to
                the initial-letter avatar if S2 fails or returns nothing. */}
            <div
              className="h-14 w-14 shrink-0 rounded-lg border border-sky-900/40 bg-[#0b1426] flex items-center justify-center overflow-hidden"
            >
              <DomainFavicon domain={headerMeta.domain} size={36} className="rounded" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-[22px] font-bold leading-tight tracking-tight text-neutral-100">
                  {headerMeta.name}
                </h1>
                {apiData?.grade && (
                  <span
                    className={`inline-flex items-center justify-center rounded border ${gradeTone(apiData.grade).border} ${gradeTone(apiData.grade).bg} px-2 py-0.5 text-[12px] font-bold ${gradeTone(apiData.grade).text}`}
                  >
                    {apiData.grade}
                  </span>
                )}
                {apiData?.score !== undefined && (
                  <span className="inline-flex items-center rounded-full border border-sky-900/40 bg-sky-500/10 px-2 py-0.5 text-[10.5px] text-sky-200 tabular-nums">
                    {displayRiskScore(apiData.score)}
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex items-center gap-x-3 gap-y-1 flex-wrap text-neutral-300">
                <MetaItem label="Industry" value={headerMeta.industry} />
                <span className="text-neutral-700">|</span>
                <MetaItem label="Size" value={headerMeta.size} />
                <span className="text-neutral-700">|</span>
                <MetaItem
                  label="HQ Location"
                  value={headerMeta.hqLocation}
                  valueExtra={
                    <span className="text-[14px] leading-none">
                      {FLAG[headerMeta.country] || ""}
                    </span>
                  }
                />
                <span className="text-neutral-700">|</span>
                <MetaItem label="Domain" value={headerMeta.domain} />
              </div>
            </div>
          </div>
          {apiData?.computed_at && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                Last Computed
              </div>
              <div className="mt-0.5 text-[12.5px] text-neutral-200 tabular-nums">
                {apiData.computed_at}
              </div>
              <div className="text-[11px] text-neutral-500">
                {apiData.cached ? "Cached" : "Fresh"}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Tab strip — same look used on Watchlist Events / Threat Feeds: thin
          divider strip beneath the page header, light underline on the active
          tab, vertical hairline dividers between tabs. No icons. */}
      <div
        className="border-b border-white/10 bg-[#000414]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 py-2">
          <nav
            className="flex items-center gap-5 flex-wrap"
            role="tablist"
            aria-label="Vendor view"
          >
            {[
              { key: "overview", label: "Overview" },
              { key: "credentials", label: "Credentials" },
            ].map((opt, i) => {
              const active = activeTab === opt.key;
              return (
                <Fragment key={opt.key}>
                  {i > 0 && (
                    <span aria-hidden className="h-3.5 w-px bg-neutral-700/60 -mb-px" />
                  )}
                  <button
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(opt.key)}
                    className={`py-2 text-sm font-medium transition whitespace-nowrap -mb-px border-b-[3px] ${
                      active
                        ? "text-white border-neutral-200"
                        : "text-neutral-400 hover:text-white border-transparent"
                    }`}
                  >
                    {opt.label}
                  </button>
                </Fragment>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Body */}
      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="supplychain"
          onChange={() => {}}
        />

        <main className="flex-1 min-w-0 px-4 md:px-6 pt-4 pb-10 space-y-4">
          {gateActive && <SupplierUpgradeRequiredCta />}
          {!gateActive && activeTab === "overview" && (
            <>
              {scoreState.loading && !apiData && (
                <div className={`${panel} px-6 py-10 text-center`}>
                  <Loader2 className="h-5 w-5 animate-spin text-sky-300 mx-auto" />
                  <div className="mt-2 text-[12.5px] text-neutral-400">
                    Computing supplier risk score…
                  </div>
                </div>
              )}

              {!scoreState.loading && scoreState.error && !apiData && (
                <div className={`${panel} px-5 py-4`}>
                  <div className="flex items-start gap-2 text-rose-200">
                    <AlertTriangle className="h-4 w-4 mt-[2px]" />
                    <div>
                      <div className="text-[13px] font-semibold">Couldn't load risk score</div>
                      <div className="mt-0.5 text-[12.5px] text-rose-300/90">
                        {scoreState.error}
                      </div>
                      <button
                        type="button"
                        onClick={() => fetchScore({ force: false })}
                        className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/15 hover:bg-sky-500/25 px-2.5 py-1 text-[12px] text-sky-100"
                      >
                        <RefreshCw className="h-3 w-3" />
                        Retry
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {apiData && (
                <>
                  <ExecutiveSummaryCard data={apiData} />
                  <RiskProfileCard data={apiData} />
                  <LatestCorporateExposuresPanel data={apiData} />
                </>
              )}
            </>
          )}

          {!gateActive && activeTab === "credentials" && (
            <CredentialsTab decoded={decoded} onView={handleCredentialView} />
          )}
        </main>
      </div>

      {/* Per-record detail sidebar. Mounted at root so it overlays the page
          (and the global sidebar) regardless of which tab is active. */}
      <DetailSidebar
        open={detailOpen}
        row={detailRow}
        initialParams={detailInitialParams}
        jwtToken={getJwtToken()}
        onClose={() => setDetailOpen(false)}
      />
    </div>
  );
}
